import { useCallback, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { shownRows, statusState, type BranchGroup } from '../lib/branchNetwork'
import { explain } from '../lib/staffErrors'
import type { SetupStatusLine } from '../lib/types'
import { Pager, usePaged } from '../ui/Pager'
import { EmptyState } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { BranchGroups, GroupDrawer, type GroupDraft } from './BranchGroups'
import { SETUP_CHANGED } from './setupChanged'
import { useFlash } from './sectionKit'
import { IconTile, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, useOpenRecord } from './settingsKit'
import '../styles/org.css'

/** Every branch at once: what each floor runs, and the groups. */

export function Network({
  organisation,
  canShape,
}: {
  organisation?: string
  /** Whether this person may add, edit or delete groups: false when they are
   *  confined to named branches (org.Staff.PinnedToBranches,
   *  internal/api/branchgroups.go), the line the server itself draws around
   *  handleCreateBranchGroup and its siblings. Reaching this section at all
   *  already means branch.manage, so that half of the server's check is never
   *  the reason this is false — only the confinement is. */
  canShape: boolean
}) {
  const { lang, say } = useStaffText()
  const [rows, setRows] = useState<SetupStatusLine[]>([])
  const [groups, setGroups] = useState<BranchGroup[]>([])
  // Where the first answer about groups stands: until it comes BranchGroups says
  // "Loading…" rather than "No groups yet", and after a refusal it says neither,
  // because the section's own error above it is the only true sentence.
  const [groupsState, setGroupsState] = useState<'loading' | 'loaded' | 'failed'>('loading')
  const [groupId, setGroupId] = useState('')
  const [find, setFind] = useState('')
  const [error, setError] = useState('')
  const { flash, toast } = useFlash()
  // A group opens on a page of its own: #network/<group id>, #network/new.
  const rec = useOpenRecord(groups, (g) => g.id, groupsState === 'loaded')
  const groupDraft: GroupDraft | null = rec.isNew
    ? { name: '', branchIds: [] }
    : rec.row
      ? { id: rec.row.id, name: rec.row.name, branchIds: rec.row.branchIds }
      : null

  const load = useCallback(async () => {
    try {
      const [status, listed] = await Promise.all([api.setupStatus(organisation), api.branchGroups(organisation)])
      setRows(status.branches)
      setGroups(listed.groups)
      setGroupsState('loaded')
      setError('')
    } catch (err) {
      // A reload that fails after an answer keeps the groups it has; a first
      // load that fails stops saying "Loading…" about an answer that is not
      // coming, and says nothing in its place.
      setGroupsState((was) => (was === 'loaded' ? was : 'failed'))
      setError(explain(err, lang, 'network.notRead'))
    }
  }, [organisation, lang])

  useEffect(() => {
    void load()
  }, [load])

  // A save anywhere else in Administration changes what these rows say, and so
  // does this section's own group drawer.
  useEffect(() => {
    const onChange = () => void load()
    window.addEventListener(SETUP_CHANGED, onChange)
    return () => window.removeEventListener(SETUP_CHANGED, onChange)
  }, [load])

  const group = groups.find((g) => g.id === groupId) ?? null
  const q = find.trim().toLowerCase()
  const shown = useMemo(
    () => shownRows(rows, group).filter((r) => !q || r.branchName.toLowerCase().includes(q) || r.branchCode.toLowerCase().includes(q)),
    [rows, group, q],
  )
  const paged = usePaged(shown, `${groupId}|${q}`)
  const groupNames = (ids: string[]) =>
    groups
      .filter((g) => ids.includes(g.id))
      .map((g) => g.name)
      .join(', ')

  if (canShape && groupDraft) {
    return (
      <>
        <GroupDrawer
          key={rec.code}
          draft={groupDraft}
          groups={groups}
          branches={rows}
          organisation={organisation}
          onClose={rec.close}
          flash={flash}
        />
        {toast}
      </>
    )
  }

  return (
    <>
      <PageHead title={say('admin.section.network')} line={say('network.line')} />
      {error && <Notice className="org-gap">{error}</Notice>}

      <BranchGroups
        branches={rows}
        groups={groups}
        state={groupsState}
        canShape={canShape}
        onOpen={(g) => rec.open(g)}
        onAdd={() => rec.open()}
      />

      <ListCard
        title={say('network.branches')}
        count={say('network.branchCount', { n: rows.length })}
        search={find}
        onSearch={setFind}
        searchLabel={say('org.br.find')}
        chips={[
          { value: '', label: say('network.allBranches'), count: rows.length },
          ...groups.map((g) => ({ value: g.id, label: g.name, count: g.branchIds.length })),
        ]}
        chip={groupId}
        onChip={setGroupId}
        foot={<Pager {...paged.pager} />}
      >
        {shown.length > 0 && (
          <ListTable
            label={say('network.branches')}
            cols="minmax(0, 1.4fr) 190px"
            head={[say('settings.branches.col.branch'), say('network.col.floor')]}
          >
            {paged.rows.map((row) => {
              const state = statusState(row, lang)
              return (
                <ListRow key={row.branchId} off={!row.active}>
                  <NameCell
                    tile={<IconTile text={row.branchCode.slice(0, 4)} tone={6} size="sm" />}
                    name={
                      <>
                        {row.branchName}
                        {row.operationProfileName && <span className="org-code">{row.operationProfileName}</span>}
                      </>
                    }
                    sub={groupNames(row.groupIds) || say('org.net.noGroup')}
                  />
                  <span className="org-floor" data-tone={state.tone}>
                    {state.label}
                  </span>
                </ListRow>
              )
            })}
          </ListTable>
        )}
        {q && shown.length === 0 && <EmptyState title={say('org.br.noMatch', { q: find.trim() })} />}
        {groupsState === 'loaded' && shown.length === 0 && group && !q && (
          <EmptyState title={say('org.net.groupEmpty', { name: group.name })} />
        )}
      </ListCard>

      {toast}
    </>
  )
}
